import { enableES5 } from 'immer';

import { isEnabled } from 'Config';

// Webkit Safari does not implement this yet:
// https://bugs.webkit.org/show_bug.cgi?id=71270
// https://caniuse.com/mdn-api_htmlcanvaselement_toblob
export const loadCanvasToBlobPolyfill = async () => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!HTMLCanvasElement.prototype.toBlob) {
    await import('blueimp-canvas-to-blob' /* webpackChunkName: "polyfill-canvas-to-blob" */);
  }
};

const isRafSupported = () =>
  ['r', 'webkitR', 'mozR', 'msR', 'oR'].some((p) => !!window[`${p}equestAnimationFrame`]);

// https://caniuse.com/requestanimationframe
export const loadRafPolyfill = async () => {
  if (!isRafSupported()) {
    await import('raf/polyfill' /* webpackChunkName: "polyfill-raf" */);
  }
};

type WindowOptionalFetch = Omit<typeof window, 'fetch'> & {
  fetch?: typeof window.fetch;
};

export const loadFetchPolyfill = async () => {
  // Cypress tests don't work with fetch yet, so we need to force a polyfill
  if (__QA__) {
    delete (window as unknown as WindowOptionalFetch).fetch;
  }

  // https://caniuse.com/?search=fetch
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!window.fetch) {
    await import('whatwg-fetch' /* webpackChunkName: "polyfill-fetch" */);
  }
};

export const loadGlobalThisPolyfill = async () => {
  // https://caniuse.com/?search=globalthis Safari < 12.2
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!window.globalThis) {
    await import('core-js/es/global-this' /* webpackChunkName: "polyfill-global-this" */);
  }
};

// https://caniuse.com/intersectionobserver
export const loadISOPolyfill = async () => {
  // Exits early if all IntersectionObserver and IntersectionObserverEntry
  // features are natively supported.
  if (
    'IntersectionObserver' in window &&
    'IntersectionObserverEntry' in window &&
    'intersectionRatio' in window.IntersectionObserverEntry.prototype
  ) {
    // Minimal polyfill for Edge 15's lack of `isIntersecting`
    // See: https://github.com/w3c/IntersectionObserver/issues/211
    if (!('isIntersecting' in window.IntersectionObserverEntry.prototype)) {
      Object.defineProperty(window.IntersectionObserverEntry.prototype, 'isIntersecting', {
        get() {
          return this.intersectionRatio > 0;
        },
      });
    }
    return;
  }
  await import('intersection-observer' /* webpackChunkName: "polyfill-intersection-observer" */);
};

// IE https://caniuse.com/#feat=url
export const loadUrlPolyfill = async () => {
  // https://caniuse.com/?search=URL
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!window.URL) {
    await import('core-js/features/url' /* webpackChunkName: "polyfill-url" */);
  }
  // Android 4.4
  // https://caniuse.com/?search=URLSearchParams
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!window.URLSearchParams) {
    await import(
      'core-js/features/url-search-params' /* webpackChunkName: "polyfill-url-search-params" */
    );
  }

  // https://caniuse.com/mdn-api_urlpattern
  // @ts-expect-error - not yet in TS definition
  if (!window.URLPattern) {
    await import('urlpattern-polyfill' /* webpackChunkName: "polyfill-url-pattern" */);
  }
};

export const loadImmerPolyfill = async () => {
  if (!isEnabled('modernBuild')) {
    enableES5();
  }
};

export const loadInertPolyfill = async () => {
  if (!('inert' in HTMLElement.prototype)) {
    await import('wicg-inert' /* webpackChunkName: "polyfill-inert-polyfill" */);
  }
};

export const loadArrayAtPolyfill = async () => {
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/at#browser_compatibility
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!Array.prototype.at) {
    await import('core-js/features/array/at' /* webpackChunkName: "polyfill-array-at" */);
  }
};

export const loadStringAtPolyfill = async () => {
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/at#browser_compatibility
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!String.prototype.at) {
    await import('core-js/features/string/at' /* webpackChunkName: "polyfill-string-at" */);
  }
};
